import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

const Upload = ({ width = "100%", onFilesSelected }) => {
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState({});
  const [completedFiles, setCompletedFiles] = useState({});

  const handleFilesAdded = (newFilesArray) => {
    setFiles((prev) => [...prev, ...newFilesArray]);
    onFilesSelected && onFilesSelected([...files, ...newFilesArray]);
    // Initialize progress
    newFilesArray.forEach((file) => {
      setProgress((prev) => ({ ...prev, [file.name]: 0 }));
    });
  };

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    handleFilesAdded(newFiles);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const newFiles = Array.from(event.dataTransfer.files);
    handleFilesAdded(newFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDelete = (fileName) => {
    setFiles((prev) => prev.filter((f) => f.name !== fileName));
    setProgress((prev) => {
      const copy = { ...prev };
      delete copy[fileName];
      return copy;
    });
    setCompletedFiles((prev) => {
      const copy = { ...prev };
      delete copy[fileName];
      return copy;
    });
  };

  const simulateUpload = useCallback(() => {
    files.forEach((file) => {
      const interval = setInterval(() => {
        setProgress((prev) => {
          const current = prev[file.name] || 0;
          if (current >= 100) {
            clearInterval(interval);
            setCompletedFiles((comp) => ({
              ...comp,
              [file.name]: true,
            }));
            return prev;
          }
          return { ...prev, [file.name]: current + 1 };
        });
      }, 100);
    });
  }, [files]);

  useEffect(() => {
    if (files.length > 0) {
      simulateUpload();
    }
  }, [files, simulateUpload]);

  return (
    <div
      style={{ width, display: "flex", flexDirection: "column", gap: "24px" }}
    >
      {/* Drop area */}
      <div
        style={{
          border: "2px dashed #E2E8F0",
          borderRadius: "12px",
          padding: "32px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px",
          background: "#F8FAFC",
          cursor: "pointer",
          transition: "border-color 0.15s, background 0.15s",
          position: "relative",
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#2563EB";
          e.currentTarget.style.background = "#F1F5FF";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "#E2E8F0";
          e.currentTarget.style.background = "#F8FAFC";
        }}
      >
        <input
          type="file"
          multiple
          onChange={handleFileChange}
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0,
            cursor: "pointer",
          }}
        />
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "8px",
            background: "#FFFFFF",
            border: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src="assets/document-upload.svg"
            alt="upload"
            style={{ width: "20px" }}
          />
        </div>
        <div style={{ textAlign: "center" }}>
          <span style={{ fontWeight: 600, color: "#0F172A", fontSize: "14px" }}>
            Click to upload
          </span>
          <span style={{ color: "#64748B", fontSize: "14px" }}>
            {" "}
            or drag and drop
          </span>
        </div>
        <p style={{ fontSize: "12px", color: "#94A3B8", margin: 0 }}>
          (Max. File Size: 20 MB)
        </p>
      </div>

      {/* File list */}
      {files.map((file) => (
        <div
          key={file.name}
          style={{
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
            padding: "16px",
            background: "#F8FAFC",
            display: "flex",
            gap: "12px",
            alignItems: "center",
          }}
        >
          <img
            src="assets/pdf.svg"
            alt="pdf"
            style={{ width: "40px", height: "40px" }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "4px",
              }}
            >
              <span
                style={{ fontWeight: 600, fontSize: "14px", color: "#0F172A" }}
              >
                {file.name}
              </span>
              <img
                src="assets/delete.svg"
                alt="Delete"
                onClick={() => handleDelete(file.name)}
                style={{ width: "20px", height: "20px", cursor: "pointer" }}
              />
            </div>
            <span style={{ fontSize: "13px", color: "#64748B" }}>
              {(file.size / 1024).toFixed(2)} KB
            </span>
            {!completedFiles[file.name] && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "8px",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: "8px",
                    borderRadius: "4px",
                    background: "#E2E8F0",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${progress[file.name]}%`,
                      height: "100%",
                      background: "#2563EB",
                      borderRadius: "4px",
                      transition: "width 0.1s linear",
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#0F172A",
                  }}
                >
                  {progress[file.name]}%
                </span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

Upload.propTypes = {
  width: PropTypes.string,
  onFilesSelected: PropTypes.func,
};

export default Upload;
